<template>
    <!--
        @pointerdown.stop: sin esto, el pointerdown de CUALQUIER click dentro del
        popup (Ampliar, Cerrar, prev/next) burbujea hasta el viewport de OpenLayers
        (el popup vive dentro de su .ol-overlaycontainer-stopevent, que es hijo del
        viewport). OL 6.15.1 solo filtra pointerdown/wheel/keydown para ese
        contenedor, pero NO el singleclick emulado que emite 250ms después — ese
        singleclick llegaba intacto a selectService.js y disparaba una consulta
        WMS con la coordenada del click en el botón, "reemplazando" la selección
        actual (por eso el popup/panel se cerraba solo ~1s después de "Ampliar").
    -->
    <div ref="popup" class="ol-popup feature-popup" :class="{ 'feature-popup--hidden': !hasFeatures || maximized }"
        @pointerdown.stop>
        <div class="popup-header">
            <div v-if="currentFeature" class="popup-title">
                <h6 class="layer-title" :title="popupTitle">{{ popupTitle }}</h6>
                <span v-if="featureTitle" class="layer-subtitle" :title="getFeatureLayerName(currentFeature)">{{ getFeatureLayerName(currentFeature) }}</span>
            </div>
            <div class="popup-header-actions">
                <button
                    v-if="currentFeature"
                    type="button"
                    class="popup-icon-btn"
                    title="Ampliar"
                    aria-label="Ampliar detalle"
                    @click="openMaximized"
                >
                    <font-awesome-icon icon="expand" />
                </button>
                <button
                    type="button"
                    class="popup-icon-btn"
                    title="Cerrar"
                    aria-label="Cerrar"
                    @click="close"
                >
                    <font-awesome-icon icon="times" />
                </button>
            </div>
        </div>

        <div class="popup-content">
            <div v-if="currentFeature">
                <div class="attributes-table-container">
                    <table class="table table-striped table-sm attributes-table">
                        <tbody>
                            <tr v-for="(value, key) in displayProperties" :key="key">
                                <td class="attr-key" :title="key">{{ key }}</td>
                                <td class="attr-value" :title="propertyTitle(value)">
                                    <AttachmentGallery
                                        v-if="isTerrainAttachmentValue(value)"
                                        compact
                                        :attachment-ids="value.attachmentIds"
                                        :attachment-names="value.attachmentNames"
                                        :kind="value.kind"
                                        :label="value.label"
                                    />
                                    <span v-else v-html="sanitizeHtml(value)"></span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-else class="no-data">
                <p>No se encontraron resultados.</p>
            </div>
        </div>

        <div class="popup-nav" v-if="features.length > 0">
            <button class="btn-nav" :disabled="currentIndex === 0" @click="prevFeature" v-if="features.length > 1">
                <font-awesome-icon icon="chevron-left" />
            </button>

            <span class="count-span">
                {{ currentIndex + 1 }} / {{ features.length }}
            </span>

            <button class="btn-nav" :disabled="currentIndex === features.length - 1" @click="nextFeature"
                v-if="features.length > 1">
                <font-awesome-icon icon="chevron-right" />
            </button>
        </div>
    </div>
</template>

<script>
import Overlay from 'ol/Overlay';
import { selectService, getFeatureLayerName, getFeatureProperties, getFeatureTitle, isTerrainAttachmentValue } from '@/services/map/selectService';
import { mapService } from '@/services/map/mapService';
import AttachmentGallery from '@/components/terrain/AttachmentGallery.vue';
import { sanitizeHtml, stripHtml } from '@/services/helpers/displayValueHelper';

export default {
    name: 'FeaturePopup',
    components: { AttachmentGallery },
    data() {
        return {
            overlay: null,
        }
    },
    computed: {
        // Reactive state from the service — currentIndex/maximized viven en
        // selectService.state (no como data local) para quedar en sincronía
        // con FeaturePopupExpanded.vue, el panel lateral que "Ampliar" abre:
        // ambos componentes deben mostrar siempre la misma feature.
        features() {
            return selectService.state.features;
        },
        location() {
            return selectService.state.location;
        },
        errors() {
            return selectService.state.errors;
        },
        currentIndex() {
            return selectService.state.currentIndex;
        },
        maximized() {
            return selectService.state.maximized;
        },
        hasFeatures() {
            return this.features && this.features.length > 0;
        },
        currentFeature() {
            if (!this.hasFeatures) return null;
            return this.features[this.currentIndex];
        },
        displayProperties() {
            return getFeatureProperties(this.currentFeature);
        },
        // QA-206: el campo configurado como `titulo` manda; el nombre de la
        // capa pasa a subtítulo. Sin campo titulo, el nombre de capa sigue
        // siendo el título como antes.
        featureTitle() {
            return getFeatureTitle(this.currentFeature);
        },
        popupTitle() {
            return this.featureTitle || getFeatureLayerName(this.currentFeature);
        }
    },
    watch: {
        location(newLoc) {
            if (this.overlay) {
                this.overlay.setPosition(newLoc);
            }
        },
        errors(list) {
            if (!list || !list.length) return;
            this.$store.dispatch('toast/show', {
                message: `No se pudo consultar: ${list.join(', ')} (posible acceso restringido/privado, formato no soportado por el servidor, o error de conexión).`,
                type: 'error'
            });
        }
    },
    mounted() {
        this.initOverlay();
    },
    beforeDestroy() {
        const map = mapService.get();
        if (map) {
            if (this.overlay) map.removeOverlay(this.overlay);
            map.un('postrender', this.ensureAttached);
        }
    },
    methods: {
        getFeatureLayerName,
        isTerrainAttachmentValue,
        sanitizeHtml,
        propertyTitle(value) {
            return isTerrainAttachmentValue(value) ? `${value.attachmentIds.length} adjunto(s)` : stripHtml(value);
        },
        initOverlay() {
            const map = mapService.get();
            if (!map) return;

            this.overlay = new Overlay({
                element: this.$el,
                autoPan: true,
                autoPanAnimation: {
                    duration: 250,
                },
            });

            map.addOverlay(this.overlay);
            // DataContainer.vue alterna hermanos condicionales de este componente
            // (FeaturePopupExpanded/AttributesTable/GeoprocessingChart vía v-if).
            // Al reconciliar esa lista de hijos, Vue en ciertos casos reinserta el
            // nodo raíz de este componente en su posición "natural" del árbol
            // (dentro de .data-container) aunque OL ya lo hubiera movido dentro de
            // su propio contenedor de overlays — el popup queda entonces
            // posicionado por CSS relativo al viewport completo en vez de a la
            // feature (terminaba siempre pegado abajo a la izquierda). Se revisa
            // en cada postrender (igual que el propio Overlay) y se re-adjunta si
            // Vue lo movió.
            map.on('postrender', this.ensureAttached);
        },
        ensureAttached() {
            if (this.overlay && this.$el && !this.overlay.element.contains(this.$el)) {
                this.overlay.element.appendChild(this.$el);
            }
        },
        close() {
            selectService.clearResults();
        },
        nextFeature() {
            if (this.currentIndex < this.features.length - 1) {
                selectService.setCurrentIndex(this.currentIndex + 1);
            }
        },
        prevFeature() {
            if (this.currentIndex > 0) {
                selectService.setCurrentIndex(this.currentIndex - 1);
            }
        },
        // openMaximized cierra los otros paneles flotantes de DataContainer.vue
        // que pudieran estar abiertos en este momento (tabla de atributos,
        // resultado de geoproceso) antes de mostrar el panel lateral de
        // detalle, para que solo haya un panel de este tipo a la vez.
        openMaximized(e) {
            e.preventDefault();
            e.stopPropagation();
            this.$store.dispatch('attributeTable/closeTable');
            this.$store.dispatch('setMapToolAction', { tool: 'geoprocessing', action: 'cleanResults', data: {} });
            selectService.setMaximized(true);
        },
    }
}
</script>

<style scoped lang="scss">
.feature-popup {
    background-color: white;
    filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    bottom: 12px;
    left: -50px;
    min-width: 300px;
    // Sin tope, una tabla con columnas anchas podía terminar más ancha que
    // el viewport; min() la acota al menor entre un ancho cómodo de lectura
    // (más generoso que antes, para que el contenido respire si lo amerita)
    // y el viewport real, con margen para no pegarse a los bordes.
    // #{"min"} escapa la función: libsass interpreta min() como la función de Sass
    // y falla al recibir un calc(). Interpolado sale literal al CSS en ambas
    // implementaciones (libsass y dart-sass).
    max-width: #{"min"}(560px, calc(100vw - 24px));
    font-family: 'Inter', sans-serif;
    color: #334155;
    display: flex;
    flex-direction: column;
    gap: 10px;

    // Arrow styling (pseudo-elements)
    &:after,
    &:before {
        top: 100%;
        border: solid transparent;
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
    }

    &:after {
        border-top-color: white;
        border-width: 10px;
        left: 48px;
        margin-left: -10px;
    }

    &:before {
        border-top-color: #e2e8f0;
        border-width: 11px;
        left: 48px;
        margin-left: -11px;
    }
}

// visibility (no display:none/v-show): el elemento raíz es el que sostiene
// el ol/Overlay — ocultarlo con display:none le hacía perder la posición de
// referencia en el mapa al volver a mostrarlo; visibility conserva su caja
// y tamaño medibles por OpenLayers, solo lo hace invisible/no interactivo.
.feature-popup--hidden {
    visibility: hidden;
    pointer-events: none;
}

.popup-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.popup-header-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    // Alinea la fila de botones con la línea base del título en vez de con
    // el borde superior del popup.
    margin-top: -2px;
}

.popup-icon-btn {
    background: transparent;
    border: none;
    border-radius: 4px;
    color: #64748b;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    padding: 4px 6px;
    transition: background-color 0.2s, color 0.2s;

    &:hover {
        background-color: #eff6ff;
        color: #3b82f6;
    }
}

// min-width:0 permite que el título (hijo de un flex) se encoja y corte con
// ellipsis en vez de empujar los botones fuera del popup.
.popup-title {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.layer-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    border-bottom: 2px solid #3b82f6;
    padding-bottom: 5px;
    display: inline-block;
    // El título nunca debe forzar el ancho del popup por sí solo; se corta
    // si el nombre de la capa es muy largo.
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

// QA-206: cuando el título es el campo configurado de la feature, el nombre
// de la capa baja acá como contexto.
.layer-subtitle {
    margin-top: 3px;
    font-size: 0.72rem;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.popup-content {
    // El scroll aplica SOLO acá, nunca al título (arriba, fuera de esta
    // caja): antes el título vivía dentro de este mismo div y se perdía de
    // vista al scrollear una tabla larga.
    max-height: 250px;
    overflow-y: auto;
}

.attributes-table-container {
    background: #f8fafc;
    border-radius: 4px;
    overflow: hidden;
}

.attributes-table {
    margin-bottom: 0;
    font-size: 0.85rem;
    width: 100%;
    // table-layout:fixed + ancho explícito (no max-width) en ambas columnas
    // es lo que evita que, al crecer el popup, el valor se superponga
    // sobre la key en vez de recortarse dentro de su propia columna.
    table-layout: fixed;

    td {
        padding: 5px 8px;
        border-bottom: 1px solid #e2e8f0;
        // QA-205: nombres y valores largos se ajustan en varias líneas en vez
        // de truncarse a una sola; el scroll vertical de .popup-content ya
        // acota la altura total del popup.
        word-break: break-word;
        vertical-align: top;
    }

    .attr-key {
        font-weight: 600;
        color: #475569;
        width: 38%;
        background-color: #f1f5f9;
    }

    .attr-value {
        color: #1e293b;
        width: 62%;
    }
}

.popup-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 5px;
    border-top: 1px solid #e2e8f0;
    padding-top: 10px;
}

.count-span {
    font-size: 0.85rem;
    font-weight: 600;
    color: #64748b;
}

.btn-nav {
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 4px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.8rem;
    transition: background 0.2s;

    &:hover:not(:disabled) {
        background-color: #2563eb;
    }

    &:disabled {
        background-color: #cbd5e1;
        cursor: not-allowed;
    }
}

.no-data {
    color: #64748b;
    font-style: italic;
    text-align: center;
    padding: 10px;
}
</style>
